/**
 * Business Site V2 — 路由配置
 * 基于 V32 L3710-L3767 前端路由定义
 *
 * 独立域名部署（business.dynmbrain.com），路由根路径为 /
 * 权限格式：platform_business:{module}:{action}
 */

import type { RouteRecordRaw } from 'vue-router'

// ════════════════════════════════════════════════════════════
// AI 官网（GEO 站点生成器）路由
// /site-builder → 旧版 SiteBuilderFullPage.vue（保留用于对比）
// /ai-site     → 新版顶级路由（AiSiteLayout 独立全屏直连，保留）
// /ai-site-workbench → 新版嵌入版（BusinessAiSiteLayout 左 Sidebar + 右 SiteDashboard）
// ════════════════════════════════════════════════════════════
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('../App.vue'),
    meta: { requiresAuth: true },
    children: [
      // ── 登录页（独立页面，复用 platform/pages/Login.vue）
      // SSOT: 所有平台统一用 /login 路径，authService.logout() 跳本平台 /login
      {
        path: 'login',
        name: 'BusinessLogin',
        component: () => import('../../platform/pages/Login.vue'),
        meta: { title: '登录', requiresAuth: false },
      },
      // ── 默认路径 → /chat（V8.8.4 修复白屏：访问 / 时 router-view 空白）
      {
        path: '',
        redirect: '/chat',
      },
      // ── 路径化导航（v2.1 2026-07-17）──
      // /chat                          → 纯对话
      // /chat/knowledge                → 知识库操作台（默认 hub）
      // /chat/knowledge/brand_overview → 知识库-品牌概览
      // /chat/knowledge/draft          → 知识库-草稿箱
      // /chat/dashboard                → 投喂驾驶舱全屏
      // /chat/brand_corpus             → 对话+品牌语料并排
      // :view(.*)* 是 catch-all，可匹配任意段数（route.params.view 为 string[]）
      {
        path: 'chat/:view(.*)*',
        name: 'BusinessChat',
        component: () => import('../views/ChatView.vue'),
        meta: { title: '对话', keepAlive: true, permissions: ['platform_business:chat:view'] },
      },
      // ── 知识库 V3（主页面 — 替换旧版 v4.1）─
      {
        path: 'knowledge',
        name: 'BusinessKnowledge',
        component: () => import('../views/knowledge/KnowledgeHomeV3.vue'),
        meta: { title: '知识库', keepAlive: true, permissions: ['platform_business:knowledge:view'] },
      },
      // ── 兼容旧 V3 子路径 → 重定向到主知识库页面 ─
      {
        path: 'knowledge/v3',
        redirect: '/knowledge',
      },
      // 🗑 旧版知识库独立入口（过渡期保留，2026-07-24 后清理）
      // {
      //   path: 'knowledge/legacy',
      //   name: 'BusinessKnowledgeLegacy',
      //   component: () => import('../views/knowledge/KnowledgeHome.vue'),
      //   meta: { title: '知识库(旧版)', permissions: ['platform_business:knowledge:view'] },
      // },
      // ── ⚠️ 旧版知识图谱（已注销，已被V3替换 /knowledge/v3 → 点「知识图谱」Tab）──
      // {
      //   path: 'knowledge/graph',
      //   name: 'BusinessKnowledgeGraph',
      //   component: () => import('../views/knowledge/components/graph/KnowledgeGraph.vue'),
      //   meta: { title: '知识图谱(旧版)', keepAlive: false, permissions: ['platform_business:knowledge:view'] },
      // },
      // ── ⚠️ 知识图谱 Demo（开发调试用，已注销）──
      // {
      //   path: 'knowledge/graph-demo',
      //   name: 'BusinessKnowledgeGraphDemo',
      //   component: () => import('../views/knowledge/KnowledgeGraphDemo.vue'),
      //   meta: { title: '知识图谱Demo', keepAlive: false, permissions: ['platform_business:knowledge:view'] },
      // },
      // {
      //   path: 'knowledge/graph-demo-v1',
      //   name: 'BusinessKnowledgeGraphDemoV1',
      //   component: () => import('../views/knowledge/KnowledgeGraphDemoV1.vue'),
      //   meta: { title: '知识图谱DemoV1', keepAlive: false, permissions: ['platform_business:knowledge:view'] },
      // },
      {
        path: 'dashboard',
        name: 'BusinessDashboard',
        component: () => import('../views/DashboardView.vue'),
        meta: { title: '投喂驾驶舱', permissions: ['platform_business:dashboard:view'] },
      },
      {
        path: 'toolbox',
        name: 'BusinessToolbox',
        component: () => import('../views/ToolboxView.vue'),
        meta: { title: '万能工具箱', permissions: ['platform_business:toolbox:view'] },
      },
      {
        path: 'billing',
        name: 'BusinessBilling',
        component: () => import('../views/BillingView.vue'),
        meta: { title: '充值与账单', permissions: ['platform_business:billing:view'] },
      },
      {
        path: 'settings',
        name: 'BusinessSettings',
        component: () => import('../views/SettingsView.vue'),
        meta: { title: '设置', permissions: ['platform_business:settings:view'] },
      },
      {
        path: 'publish-channel',
        name: 'BusinessPublishChannel',
        component: () => import('../views/PublishChannelView.vue'),
        meta: { title: '发布渠道', permissions: ['platform_business:channel:view'] },
      },
      {
        path: 'distribution/strategy',
        name: 'BusinessDistributionStrategy',
        component: () => import('../views/StrategyView.vue'),
        meta: { title: '策略管理', permissions: ['platform_business:distribution:view'] },
      },
      {
        path: 'distribution',
        name: 'BusinessDistribution',
        component: () => import('../views/DistributionWorkbench.vue'),
        meta: { title: '分发工作台', permissions: ['platform_business:distribution:view'] },
      },
      {
        path: 'distribution/:id',
        name: 'BusinessDistributionDetail',
        component: () => import('../views/DistributionDetailView.vue'),
        meta: { title: '分发详情', permissions: ['platform_business:distribution:view'] },
      },
      {
        path: 'help',
        name: 'BusinessHelp',
        component: () => import('../views/HelpView.vue'),
        meta: { title: '帮助中心', permissions: ['platform_business:help:view'] },
      },
      // ════════════════════════════════════════════════════════════
      // AI 官网 — 旧版入口（只读，不修改）
      // 新版独立入口：/ai-site → src/views/ai-official-site/
      // ════════════════════════════════════════════════════════════
      {
        path: 'site-builder',
        name: 'BusinessSiteBuilder',
        component: () => import('../views/SiteBuilderFullPage.vue'),
        meta: { title: 'AI官网', permissions: ['platform_business:site_builder:view'] },
      },
      // 旧全屏入口：保留 1 个 Sprint，最终 redirect 到 /site-builder
      {
        path: 'workbench/:type',
        name: 'BusinessFullscreenWorkbench',
        component: () => import('../views/FullscreenWorkbench.vue'),
        meta: { title: '全屏操作台', permissions: ['platform_business:workbench:view'] },
      },
      {
        path: 'report/:reportId',
        name: 'BusinessReportDetail',
        component: () => import('../views/ReportDetailView.vue'),
        meta: { title: '诊断报告', permissions: ['platform_business:diagnosis:view'] },
      },
      {
        path: 'diagnosis-history',
        name: 'BusinessDiagnosisHistory',
        component: () => import('../components/DiagnosisHistory.vue'),
        meta: { title: '诊断记录', permissions: ['platform_business:diagnosis:view'] },
      },

      // ════════════════════════════════════════
      // GEO 内容自动生成 — 草稿预览路由（验收后删除）
      // ════════════════════════════════════════
      {
        path: 'draft-geo/package-selector',
        name: 'DraftGeoPackageSelector',
        component: () => import('../__draft__/geo/01-PackageSelector.vue'),
        meta: { title: '[草稿] P1 套餐选择器' },
      },
      {
        path: 'draft-geo/hot-question-matrix',
        name: 'DraftGeoHotQuestionMatrix',
        component: () => import('../__draft__/geo/02-HotQuestionMatrix.vue'),
        meta: { title: '[草稿] P2 热问矩阵' },
      },
      {
        path: 'draft-geo/content-distributor',
        name: 'DraftGeoContentDistributor',
        component: () => import('../__draft__/geo/03-ContentDistributor.vue'),
        meta: { title: '[草稿] P3 分发中心' },
      },
      {
        path: 'draft-geo/tiered-generation',
        name: 'DraftGeoTieredGeneration',
        component: () => import('../__draft__/geo/04-TieredGenerationWizard.vue'),
        meta: { title: '[草稿] P4 分层生成' },
      },
      {
        path: 'draft-geo/site-workbench',
        name: 'DraftGeoSiteWorkbench',
        component: () => import('../__draft__/geo/05-SiteWorkbench.vue'),
        meta: { title: '[草稿] P5 站点工作台' },
      },
      {
        path: 'draft-geo/effect-dashboard',
        name: 'DraftGeoEffectDashboard',
        component: () => import('../__draft__/geo/06-GeoEffectDashboard.vue'),
        meta: { title: '[草稿] P6 效果仪表盘' },
      },
      {
        path: 'hot-question-workbench',
        name: 'HotQuestionWorkbench',
        component: () => import('../components/workbench/HotQuestionWorkbench.vue'),
        meta: { title: '热词热句工作台（完整版）' },
      },
      {
        path: 'hot-question-library',
        name: 'HotQuestionLibrary',
        component: () => import('../components/workbench/HotQuestionLibrary.vue'),
        meta: { title: '热问句编辑库（交互式）' },
      },
      // ════════════════════════════════════════════════════════════
      // V8.8 新增：AI 官网嵌入版路由（方案三路径C修订版，双入口）
      //
      // /ai-site-workbench → BusinessAiSiteLayout（左 Sidebar + 右 router-view）
      //   ├ ''                    → SiteDashboard（7 Tab 全功能）
      //   ├ 'editor/:siteId?'     → SiteEditor
      //   ├ 'history'             → SiteHistory
      //   └ 'preview/:siteId'     → SitePreview
      //
      // 与顶级 /ai-site（AiSiteLayout 独立全屏直连）职责分离：
      //   - /ai-site            = 用户直连/书签，全屏无 Sidebar
      //   - /ai-site-workbench  = 企业主平台 Sidebar 点击进入，左 Sidebar + 右 SiteDashboard
      //
      // SSOT：子路由 component 复用 ai-official-site/views/ 下的现有组件，零改动
      // ════════════════════════════════════════════════════════════
      {
        path: 'ai-site-workbench',
        component: () => import('../views/BusinessAiSiteLayout.vue'),
        meta: {
          title: 'AI官网',
          keepAlive: true,
          permissions: ['platform_business:ai_site:view'],
        },
        children: [
          {
            path: '',
            name: 'BusinessAiSiteDashboard',
            component: () => import('../../ai-official-site/views/SiteDashboard.vue'),
            meta: { title: 'AI官网', keepAlive: true },
          },
          {
            path: 'editor/:siteId?',
            name: 'BusinessAiSiteEditor',
            component: () => import('../../ai-official-site/views/SiteEditor.vue'),
            meta: { title: '站点编辑' },
          },
          {
            path: 'history',
            name: 'BusinessAiSiteHistory',
            component: () => import('../../ai-official-site/views/SiteHistory.vue'),
            meta: { title: '历史记录', keepAlive: true },
          },
          {
            path: 'preview/:siteId',
            name: 'BusinessAiSitePreview',
            component: () => import('../../ai-official-site/views/SitePreview.vue'),
            meta: { title: '站点预览' },
          },
        ],
      },
      // ── catch-all：未知路径 → /chat（V8.8.4 修复退出登录跳 /platform/login 失败）
      {
        path: ':pathMatch(.*)*',
        redirect: '/chat',
      },
    ],
  },

  // ── H5 数字名片（DE-001 V2.2 §14.4）──
  // 访客免登录顶层路由：不进 App.vue 布局壳（无 Header），requiresAuth 显式 false
  // 双挂载均生效：主应用守卫按 meta 放行；entries/business.ts 无守卫天然放行
  {
    path: '/e/:code',
    name: 'BusinessNameCard',
    component: () => import('../digital-employee/views/NameCardPage.vue'),
    meta: { title: '数字名片', requiresAuth: false },
  },

  // ── 对内军师全屏对话（DE-001 V2.2 §14.5.6-④）──
  // 登录态独立全屏页（非弹窗）：不进 App.vue 布局壳，页面自带头部
  {
    path: '/strategist/:employeeId',
    name: 'BusinessStrategistChat',
    component: () => import('../digital-employee/views/StrategistChatPage.vue'),
    meta: { title: '军师对话' },
  },
]

export const businessSiteV2Routes = routes
